import { FC, useEffect, useState } from 'react';
import styled, { CSSObject } from '@emotion/styled';
import { CancelToken } from 'axios';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  getQuestionnaireVersionList,
  questionnaireVersionListActions,
  selectQuestionnaireVersionList
} from '../../store/entities/questionnaireVersionList';
import { QcsButton } from '../common/basic/QcsButton';
import { TableRowButton } from '../common/basic/TableRowButton';
import { QcsTypography } from '../common/basic/QcsTypography';
import { CellDate } from '../common/grid/CellDate';
import { Grid } from '../common/grid/Grid';
import { QcsTableCell } from '../common/basic/QcsTableCell';
import { FaIcon } from '../common/FaIcon';
import { faCopy } from '@fortawesome/free-solid-svg-icons';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { CopyVersionToQuestionnaireModal } from './CopyVersionToQuestionnaireModal';
import { CopyVersionModal } from './CopyVersionModal';
import { questionnaireListActions } from '../../store/entities/questionnaireList';
import { useTheme } from '@mui/material';
import { faCalendarCheck } from '@fortawesome/free-regular-svg-icons';
import { QuestionnaireVersionDto } from '@api/models/questionnaire/QuestionnaireVersionDto';
import { questionnaireVersionService } from '@api/services/services';
import { PathKey, useNavigation } from '../../utils/urlPaths';

const HeaderContainer = styled.div(
  (): CSSObject => ({
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '1rem',
  }),
);

export const QuestionnaireVersion: FC = () => {
  const { t } = useTranslation();
  const questionnaireVersionList = useAppSelector(selectQuestionnaireVersionList);
  const dispatch = useAppDispatch();
  const { navigate } = useNavigation();
  const theme = useTheme();
  const { questionnaireId } = useParams();
  const [versionToCopy, setVersionToCopy] = useState<QuestionnaireVersionDto | null>(null);
  const [versionToCopyToQuestionnaire, setVersionToCopyToQuestionnaire] = useState<QuestionnaireVersionDto | null>(null);

  useEffect(() => {
    dispatch(questionnaireVersionListActions.reload(true));
  }, [dispatch]);

  const handleGetData = (cancelToken: CancelToken) => {
    dispatch(getQuestionnaireVersionList(questionnaireId!, cancelToken));
  };

  const handleAddVersionCopy = (version: QuestionnaireVersionDto) => () => {
    setVersionToCopy(version);
  };

  const handleAddVersionCopyToQuestionnaire = (version: QuestionnaireVersionDto) => () => {
    setVersionToCopyToQuestionnaire(version);
  };

  const handleDeleteVersion = (id: string) => async () => {
    await questionnaireVersionService.deleteById(id);
    handleReloadQuestionnaireVersionList();
  };

  // The backend only deletes versions whose validity has not started yet - anything else is
  // rejected with error 057 - so the delete button stays disabled for such versions.
  const canDeleteVersion = (item: QuestionnaireVersionDto) =>
    !!item.editable && DateTime.fromISO(item.validFrom).startOf('day') > DateTime.now().startOf('day');

  const handleRenderData = (item: QuestionnaireVersionDto) => {
    const deletable = canDeleteVersion(item);

    return (
      <>
        <QcsTableCell>{item.active && <FaIcon icon={faCalendarCheck} color={theme.palette.primary.main} />}</QcsTableCell>
        <CellDate value={item.validFrom} />
        <CellDate value={item.validTo} />
        <QcsTableCell align="center">
          <TableRowButton onClick={handleAddVersionCopy(item)}>
            <FaIcon icon={faCopy} />
          </TableRowButton>
        </QcsTableCell>
        <QcsTableCell align="center">
          <TableRowButton onClick={handleAddVersionCopyToQuestionnaire(item)}>
            <FaIcon icon={faCopy} />
          </TableRowButton>
        </QcsTableCell>
        <QcsTableCell align="center">
          <TableRowButton disabled={!deletable} onClick={handleDeleteVersion(item.id!)}>
            <DeleteForeverIcon color={deletable ? 'error' : undefined} />
          </TableRowButton>
        </QcsTableCell>
      </>
    );
  };

  const handleReloadQuestionnaireVersionList = () => {
    dispatch(questionnaireVersionListActions.reload(true));
    dispatch(questionnaireListActions.reload(true));
  };

  const handleRowClick = (item: QuestionnaireVersionDto) => {
    navigate(PathKey.QuestionnaireVersion, { questionnaireId: questionnaireId!, questionnaireVersionId: item.id! });
  };

  const handleAddClick = () => {
    navigate(PathKey.QuestionnaireVersion, { questionnaireId: questionnaireId!, questionnaireVersionId: 'new' });
  };

  return (
    <>
      <CopyVersionModal
        versionToCopy={versionToCopy}
        onClose={() => setVersionToCopy(null)}
        reloadQuestionnaireVersionList={handleReloadQuestionnaireVersionList}
      />
      <CopyVersionToQuestionnaireModal
        questionnaireId={questionnaireId}
        versionToCopy={versionToCopyToQuestionnaire}
        onClose={() => setVersionToCopyToQuestionnaire(null)}
        reloadQuestionnaireVersionList={handleReloadQuestionnaireVersionList}
      />
      <HeaderContainer>
        <QcsTypography variant="h5">{t('questionnaire.version')}</QcsTypography>
        <QcsButton variant="contained" onClick={handleAddClick}>
          {t('questionnaire.addVersion')}
        </QcsButton>
      </HeaderContainer>
      <Grid<QuestionnaireVersionDto>
        headers={[
          { sx: { width: '56px' } },
          { captionStr: 'questionnaire.validFrom', orderName: 'validFrom' },
          { captionStr: 'questionnaire.validTo', orderName: 'validTo' },
          {
            captionStr: 'questionnaire.copy.addVersion',
            sx: { textAlign: 'center' },
          },
          {
            captionStr: 'questionnaire.copy.versionToQuestionnaire',
            sx: { textAlign: 'center' },
          },
          {
            captionStr: 'questionnaire.deleteVersion',
            sx: { textAlign: 'center' },
          },
        ]}
        data={questionnaireVersionList}
        gridActions={questionnaireVersionListActions}
        renderData={handleRenderData}
        onRowClick={handleRowClick}
        getData={handleGetData}
        search={false}
      />
    </>
  );
};
